import { inject, Injectable } from '@angular/core';
import type { DirectUpload } from '@reconcept-platform/shared/types';
import { BugsnagClient, getFileReader } from '@reconcept-platform/shared/util';
import { Observable, type Observer, Subject } from 'rxjs';
import { ArrayBuffer } from 'spark-md5';

export interface FileUploadProgress {
  bytesLoaded: number;
  bytesTotal: number;
  bytesPercentage: number;
  status: number;
  type: 'progress' | 'load' | 'error';
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private readonly bugsnagClient = inject(BugsnagClient);

  fileUploadProgress = new Subject<FileUploadProgress>();

  // actually upload file to S3
  uploadFile(directUpload: DirectUpload, fileBlob: File): Observable<FileUploadProgress> {
    // return as cold observable:
    // all actions must stay in this observable to allow the `retry` operator to retry failed uploads
    return new Observable((observer: Observer<FileUploadProgress>) => {
      // create an XHR so that we can track progress
      const xhr = new XMLHttpRequest();

      // @ts-expect-error TS2322 type not assignable
      const getUploadProgress: (event: any) => FileUploadProgress = (event) => {
        return {
          status: xhr.status,
          bytesLoaded: event.loaded,
          bytesTotal: event.total,
          bytesPercentage: event.total && event.loaded ? event.loaded / event.total : 0,
          type: event.type,
          id: directUpload.signedId,
          name: fileBlob.name,
        };
      };

      xhr.upload.addEventListener('loadstart', (event) => {
        // initialize progress uploaded file
        this.fileUploadProgress.next(getUploadProgress(event));
      });

      xhr.upload.addEventListener('progress', (event) => {
        const uploadProgress = getUploadProgress(event);

        // send the update to any listening party
        this.fileUploadProgress.next(uploadProgress);

        observer.next(uploadProgress); // update the observable with the latest progress value
      });

      xhr.addEventListener('load', (event) => {
        if (xhr.status >= 200 && xhr.status < 300) {
          observer.next(getUploadProgress(event));
        } else if (xhr.status === 422) {
          observer.error(new Error(xhr.responseText));
        } else {
          this.handleError(directUpload, xhr);
          observer.error(getUploadProgress(event));
        }
        observer.complete();
      });

      // on error
      xhr.addEventListener('error', (error) => {
        this.handleError(directUpload, xhr);
        observer.error(getUploadProgress(error));
      });

      // the backend decides whether this points straight at S3 or via the virus-scanning proxy
      xhr.open('PUT', directUpload.uploadUrl ?? '', true);
      xhr.responseType = 'text';
      for (const key in directUpload.uploadHeaders) {
        xhr.setRequestHeader(key, directUpload.uploadHeaders[key]);
      }

      xhr.send(fileBlob);

      return () => xhr.abort();
    });
  }

  private handleError(directUpload: DirectUpload, xhr: XMLHttpRequest) {
    this.bugsnagClient?.notify(
      new Error(`An error occurred while trying to PUT ${directUpload.uploadUrl}`),
      {
        directUpload,
        xhr: {
          readyState: xhr.readyState,
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
        },
      },
      'Error',
    );
  }

  // get base 64 md5 checksum for file
  getFileChecksum(fileBlob: File): Observable<string> {
    // @ts-expect-error: Element implicitly has an 'any' type because expression of type '"mozSlice"' can't be used to index type 'File'.
    const blobSlice = File.prototype.slice || File.prototype['mozSlice'] || File.prototype['webkitSlice'],
      chunkSize = 2097152, // Read in chunks of 2MB
      chunks = Math.ceil(fileBlob.size / chunkSize),
      md5Buffer = new ArrayBuffer(),
      fileReader = getFileReader();

    let currentChunk = 0;

    function loadNext() {
      const start = currentChunk * chunkSize,
        end = start + chunkSize >= fileBlob.size ? fileBlob.size : start + chunkSize;

      fileReader.readAsArrayBuffer(blobSlice.call(fileBlob, start, end));
    }

    // start loading
    loadNext();

    return new Observable((observer) => {
      // on load of a chunk
      fileReader.onload = function ($event) {
        md5Buffer.append((<any>$event.target).result); // Append array buffer
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          const md5 = md5Buffer.end(true);
          observer.next(btoa(md5));
          observer.complete();
        }
      };
      // if an error occurs
      fileReader.onerror = (error) => observer.error(error);
    });
  }
}
